import { BookingStatus } from '@/types/booking-management.types';
import {
  BerthResponse as BerthDetails,
  Unavailability,
} from '@/types/berth-management.types';
import { IAddonData } from '@/types/addon-management.types';
import { IClubResourceData } from '@/types/club-resource.types';

export type ViewType = 'month' | 'week' | 'day';

export type AssetType = 'berth' | 'addon' | 'resource' | 'service';

import { IServiceData } from '@/types/service-management.types';

export interface CalendarEvent {
  id: string;
  title: string;
  start: string; // YYYY-MM-DD
  end?: string; // YYYY-MM-DD | undefined (open ended / single day)
  status: keyof typeof statusConfig;
  berthNumber: string;
  user: { name: string; email: string };
  isConflicting?: boolean;
  isAvailable?: boolean;
  isBlocked?: boolean;
  isExternallyReserved?: boolean;
  // Asset type information
  assetType?: AssetType;
  // Optional raw references for blocked/available event details
  berth?: BerthDetails; // Detailed berth info when event represents a berth state
  unavailability?: Unavailability; // Blocked range details when event represents a blocked berth
  addon?: IAddonData; // Addon information when event represents an addon
  resource?: IClubResourceData; // Resource information when event represents a club resource
  service?: IServiceData; // Service information when event represents a service booking
  // Additional fields for addons and services
  price?: number; // Price of the addon/service
  priceUnit?: string; // Price unit (PER_DAY, PER_HOUR, etc.)
  quantity?: number; // Quantity for service bookings
  startTime?: string; // Start time for service bookings
  endTime?: string; // End time for service bookings
}

export interface StatusConfigType {
  label: string;
  color: string;
  lightColor: string;
  textColor: string;
  borderColor: string;
  pattern?: string;
}

export const statusConfig: Record<string, StatusConfigType> = {
  confirmed: {
    label: 'Confirmed',
    color: 'bg-emerald-700',
    lightColor: 'bg-emerald-100 dark:bg-emerald-950 contrast:bg-emerald-950',
    textColor:
      'text-emerald-800 dark:text-emerald-200 contrast:text-emerald-100',
    borderColor: 'border-emerald-700',
  },
  'payment-pending': {
    label: 'Payment Pending',
    color: 'bg-sky-700',
    lightColor: 'bg-sky-100 dark:bg-sky-950 contrast:bg-sky-950',
    textColor: 'text-sky-800 dark:text-sky-200 contrast:text-sky-100',
    borderColor: 'border-sky-700',
  },
  'pending-confirmation': {
    label: 'Pending Confirmation',
    color: 'bg-orange-700',
    lightColor: 'bg-orange-100 dark:bg-orange-950 contrast:bg-orange-950',
    textColor: 'text-orange-800 dark:text-orange-200 contrast:text-orange-100',
    borderColor: 'border-orange-700',
  },
  completed: {
    label: 'Completed',
    color: 'bg-purple-700',
    lightColor: 'bg-purple-100 dark:bg-purple-950 contrast:bg-purple-950',
    textColor: 'text-purple-800 dark:text-purple-200 contrast:text-purple-100',
    borderColor: 'border-purple-700',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-gray-700',
    lightColor: 'bg-gray-300 dark:bg-gray-950 contrast:bg-gray-950',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-700',
  },
  available: {
    label: 'Available',
    color: 'bg-green-500',
    lightColor: 'bg-green-100 dark:bg-green-950 contrast:bg-green-950',
    textColor: 'text-green-800 dark:text-green-200 contrast:text-green-100',
    borderColor: 'border-green-500',
    // Uses the 'availability-stripes' class defined in styles.css (a repeating-linear-gradient background) instead of an inline Tailwind arbitrary value for easier maintenance & test stability
    pattern: 'availability-stripes',
  },

  blocked: {
    label: 'Blocked',
    color: 'bg-red-600',
    lightColor: 'bg-red-100 dark:bg-red-950 contrast:bg-red-950',
    textColor: 'text-red-800 dark:text-red-200 contrast:text-red-100',
    borderColor: 'border-red-600',
    pattern: 'blocked-stripes',
  },

  'externally-reserved': {
    label: 'Reserved outside Saaristo.online',
    color: 'bg-amber-700',
    lightColor: 'bg-amber-100 dark:bg-amber-950 contrast:bg-amber-950',
    textColor: 'text-amber-800 dark:text-amber-200 contrast:text-amber-100',
    borderColor: 'border-amber-700',
    pattern: 'external-reservation-stripes',
  },
};

export const assetTypeConfig: Record<AssetType, StatusConfigType> = {
  berth: {
    label: 'Berths',
    color: 'bg-blue-700',
    lightColor: 'bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950',
    textColor: 'text-blue-800 dark:text-blue-200 contrast:text-blue-100',
    borderColor: 'border-blue-700',
  },
  addon: {
    label: 'Add-ons',
    color: 'bg-purple-700',
    lightColor: 'bg-purple-100 dark:bg-purple-950 contrast:bg-purple-950',
    textColor: 'text-purple-800 dark:text-purple-200 contrast:text-purple-100',
    borderColor: 'border-purple-700',
  },
  resource: {
    label: 'Resources',
    color: 'bg-teal-700',
    lightColor: 'bg-teal-100 dark:bg-teal-950 contrast:bg-teal-950',
    textColor: 'text-teal-800 dark:text-teal-200 contrast:text-teal-100',
    borderColor: 'border-teal-700',
  },
  service: {
    label: 'Service',
    color: 'bg-orange-700',
    lightColor: 'bg-orange-100 dark:bg-orange-950 contrast:bg-orange-950',
    textColor: 'text-orange-800 dark:text-orange-200 contrast:text-orange-100',
    borderColor: 'border-orange-700',
  },
};

// Map backend numeric status to UI string key
export const mapBookingStatus = (status: number): CalendarEvent['status'] => {
  switch (status) {
    case BookingStatus.CONFIRMED:
      return 'confirmed';
    case BookingStatus.PENDING:
      return 'payment-pending';
    case BookingStatus.PENDING_CONFIRMATION:
      return 'pending-confirmation';
    case BookingStatus.COMPLETED:
      return 'completed';
    case BookingStatus.CANCELLED:
      return 'cancelled';
    default:
      return 'confirmed';
  }
};
